import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse, GrowthSignal } from '@nexus/shared/types';
import { toast } from './toastStore';

interface GrowthStats {
  total: number;
  new: number;
  reviewed: number;
  actioned: number;
  avgScore: number;
}

interface GrowthState {
  signals: GrowthSignal[];
  stats: GrowthStats | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  fetch: (params?: Record<string, string | number | undefined>) => Promise<void>;
  fetchStats: () => Promise<void>;
  markReviewed: (id: string) => Promise<void>;
  markActioned: (id: string) => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  clearError: () => void;
}

function convert(s: Record<string, unknown>): GrowthSignal {
  return { ...s, createdAt: fromUnix(s.createdAt as number) } as GrowthSignal;
}

export const useGrowthStore = create<GrowthState>((set) => ({
  signals: [],
  stats: null,
  total: 0,
  isLoading: false,
  error: null,

  fetch: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<GrowthSignal[]>>(`/api/growth/signals${toQueryString(params)}`);
      set({
        signals: (res.data ?? []).map(s => convert(s as unknown as Record<string, unknown>)),
        total: res.meta?.total ?? 0,
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  fetchStats: async () => {
    try {
      const res = await api.get<ApiResponse<GrowthStats>>('/api/growth/stats');
      set({ stats: res.data ?? null });
    } catch { /* ignore */ }
  },

  markReviewed: async (id) => {
    try {
      const res = await api.post<ApiResponse<GrowthSignal>>(`/api/growth/signals/${id}/reviewed`);
      const signal = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ signals: s.signals.map((g) => (g.id === id ? signal : g)) }));
      toast.success('Signal updated');
    } catch (err) {
      toast.error('Failed to mark as reviewed', (err as Error).message);
      throw err;
    }
  },

  markActioned: async (id) => {
    try {
      const res = await api.post<ApiResponse<GrowthSignal>>(`/api/growth/signals/${id}/actioned`);
      const signal = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ signals: s.signals.map((g) => (g.id === id ? signal : g)) }));
      toast.success('Signal updated');
    } catch (err) {
      toast.error('Failed to mark as actioned', (err as Error).message);
      throw err;
    }
  },

  dismiss: async (id) => {
    try {
      const res = await api.post<ApiResponse<GrowthSignal>>(`/api/growth/signals/${id}/dismiss`);
      const signal = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ signals: s.signals.map((g) => (g.id === id ? signal : g)) }));
      toast.success('Signal updated');
    } catch (err) {
      toast.error('Failed to dismiss signal', (err as Error).message);
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
